import React, { useMemo, useState, useEffect, useContext } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Grid, } from "@mui/material";
import ClearRounded from '@mui/icons-material/ClearRounded';
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import CustomLoader from "../common/CustomLoader";
import { genericGet } from "../../services/apiServices";
import { GET_IMPORT_PAYMENT_REPORT } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import { genericPost } from "../../services/authServices";
import { getColorById, getColorPlate, getCurrencyList, getPaymentType } from "../../services/CommonServices";
import CustomFilter from "../common/CustomFilter";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomButton from "../common/CustomButton";
import CustomTextField from "../common/CustomTextField";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomStatus from "../common/CustomStatus";

const title = "Import Payment Report";

const ImportPaymentReport = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [permission, setPermissions] = useState()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currencyList, setCurrencyList] = useState([]);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [paymentType, setPaymentType] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [colorlist, setColorlist] = useState([]);

    const [filterPayload, setFilterPayload] = useState({
        currencyId: '',
        paymentTypeId: '',
        commercialInvoiceNo: '',
        paymentVoucherNo: '',
        proformaInvoiceNo: '',
        shipmentId: '',
        fromDate: '',
        toDate: '',

    });
    const [searchCompany, setSearchCompany] = useState([]);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                currencyId: '',
                paymentTypeId: '',
                commercialInvoiceNo: '',
                paymentVoucherNo: '',
                proformaInvoiceNo: '',
                shipmentId: '',
                fromDate: '',
                toDate: '',

            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])
    const getCommonData = async () => {
        const currencyListData = await getCurrencyList();
        setCurrencyList(currencyListData);
        const paymentType = await getPaymentType();
        setPaymentType(paymentType);
        const colorData = await getColorPlate();
        setColorlist(colorData);

    }

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment I’d', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'proformaInvoiceNo', header: 'PI No.', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'supplierName', header: 'Supplier Name', size: 150 },

            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'paymentType', header: 'payment Type', size: 150 },
            { accessorKey: 'amount', header: 'Amount', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'conversionRate', header: 'Conversion Rate', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'amountInBaseCurrency', header: 'Amount in Base Currency', size: 200, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shippingStatus', header: 'Shipment Status', size: 150 },
        ],
        [permission]
    );
    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        setError('');
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            // const response = await genericGet({ url: GET_IMPORT_PAYMENT_REPORT });
            const response = await genericPost(GET_IMPORT_PAYMENT_REPORT, payload, {
            });
            if (response.data.success && response.data.paymentVoucher) {
                const paymentVoucherData = response.data.paymentVoucher.map((voucher, index) => {
                    const countryColorObj = getColorById(voucher?.importShipment?.supplier?.billingCountry?.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });
                    return {

                        sn: voucher?.paymentVoucherId || '',
                        index: (index + 1).toString().padStart(2, '0'),
                        date: companyDateFormat(voucher?.date) || '',

                        voucherNo: voucher?.paymentVoucherNo || '',
                        shipmentId: voucher?.importShipment?.shipmentId || '',
                        supplierName: voucher?.importShipment?.supplier?.clientCompanyName || '',
                        billingCountry: voucher?.importShipment?.supplier?.billingCountry?.name || '',
                        paymentType: voucher?.paymentType?.paymentName || '',
                        proformaInvoiceNo: voucher?.importPiNoList?.proformaInvoiceNo || '',
                        amount: voucher?.referenceAmount && voucher?.currency?.currency_symbol
                            ? `${voucher?.currency.currency_symbol} ${Number(voucher?.referenceAmount).toFixed(2)}`
                            : '',
                        amountInBaseCurrency: voucher?.amountInBaseCurrency && voucher?.baseCurrency?.currency_symbol
                            ? `${voucher?.baseCurrency?.currency_symbol} ${voucher?.amountInBaseCurrency}`
                            : '',
                        conversionRate: voucher?.conversionRate && voucher?.currency?.currency_symbol
                            ? `${voucher?.currency?.currency_symbol} ${voucher?.conversionRate}`
                            : '',
                        shippingStatus: voucher?.shippingTracker?.shippingStatus?.title || '',
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',
                    };
                });
                setData(paymentVoucherData);
                setRowCount(response.data.pagination?.totalRecords || 0);
            } else {
                setError('No payment vouchers found.');
            }
        } catch (error) {
            console.error("Fetch error:", error);
            setError('Failed to fetch payment vouchers.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, colorlist, token])

    const defaultView = 'importpaymentreport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Filter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Payment Voucher No"
                                        value={filterPayload.paymentVoucherNo}
                                        onChange={(e) => handleFilterChange('paymentVoucherNo', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Commercial Invoice"
                                        value={filterPayload.commercialInvoiceNo}
                                        onChange={(e) => handleFilterChange('commercialInvoiceNo', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Proforma Invoice No"
                                        value={filterPayload.proformaInvoiceNo}
                                        onChange={(e) => handleFilterChange('proformaInvoiceNo', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={currencyList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Currency"
                                        onChange={(event, newValue) => handleFilterChange("currencyId", newValue?.id)}
                                        value={currencyList.find((item) => item.id === filterPayload.currencyId) || null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={paymentType}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Payment Type"
                                        onChange={(event, newValue) => handleFilterChange("paymentTypeId", newValue?.id)}
                                        value={paymentType.find((item) => item.id === filterPayload.paymentTypeId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        style={cif_fob_style}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };

    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}

        </>
    );
};
export default ImportPaymentReport;

const cif_fob_style = {
    backgroundColor: '#ffff',
    color: '#007bff',
    textTransform: "capitalize",
    borderRadius: 4,
    border: '1px solid #007bff',
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: "100px"

}